import { useMemo, useState } from 'react';
import { ClipboardList, MessageSquareHeart, RefreshCw } from 'lucide-react';
import { cn } from '@/shared/lib/cn';
import { Button } from '@/shared/ui/button';
import { Card } from '@/shared/ui/card';
import { Skeleton } from '@/shared/ui/skeleton';
import { parseSurveyDescription } from '@/features/nps/public-survey/parse-description';
import { PublicSurveyI18nProvider } from '@/features/nps/public-survey/public-survey-i18n-provider';
import { PublicSurveySubmitCelebration } from '@/features/nps/public-survey/public-survey-submit-celebration';
import { PublicSurveyWizard } from '@/features/nps/public-survey/public-survey-wizard';
import type { ParticipantProfile } from '../../api/participant-api';
import { SURVEY_LANG, useParticipantSurvey } from '../../api/use-participant';
import { surveyPrefill } from '../../model/survey-prefill';
import { PromoHeroCard } from '../promo-hero-card';

interface SurveyTabProps {
  idPassbook: string;
  surveyId: string;
  /** CPF da sessão: a pesquisa já sai identificada (pula a etapa de CPF). */
  uniqueId: string;
  /** Cadastro de quem está logado: preenche nome, e-mail, celular e nascimento. */
  profile: ParticipantProfile;
  accentColor?: string;
  foregroundColor?: string;
}

/**
 * A pesquisa que a loja montou no módulo NPS (`regraCartao.idSurvey`, a mesma do
 * e-mail transacional), respondida aqui dentro. Cabeçalho enxuto (o título da
 * pesquisa + descrição recolhida) e dados pessoais já preenchidos: se todos
 * vierem do cadastro, a etapa "Seus dados" nem aparece.
 */
export function SurveyTab({ idPassbook, surveyId, uniqueId, profile, accentColor, foregroundColor }: SurveyTabProps) {
  const { data: survey, isPending, isError, refetch, isRefetching } = useParticipantSurvey(idPassbook, surveyId);
  const [answered, setAnswered] = useState(false);
  const [showDescription, setShowDescription] = useState(false);
  const initialAnswers = useMemo(
    () => (survey ? surveyPrefill(survey.questions ?? [], profile, uniqueId) : undefined),
    // Só na abertura: editar Meus dados depois não reescreve uma pesquisa em andamento.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [survey],
  );

  if (isPending) {
    return (
      <div className="mx-auto w-full max-w-xl space-y-4">
        <Skeleton className="h-20 w-full rounded-2xl" />
        <Skeleton className="h-64 w-full rounded-2xl" />
      </div>
    );
  }

  if (isError || !survey) {
    return (
      <Card className="mx-auto flex w-full max-w-xl flex-col items-center gap-3 p-8 text-center">
        <p className="font-medium">Não foi possível abrir a pesquisa.</p>
        <Button variant="outline" onClick={() => void refetch()} loading={isRefetching}>
          <RefreshCw className="size-4" />
          Tentar de novo
        </Button>
      </Card>
    );
  }

  if (['FINISHED', 'PAUSED'].includes(survey.status)) {
    return (
      <Card className="mx-auto flex w-full max-w-xl flex-col items-center gap-2 p-8 text-center">
        <span className="grid size-12 place-items-center rounded-2xl bg-muted">
          <ClipboardList className="size-6 text-muted-foreground" />
        </span>
        <p className="font-medium">Pesquisa encerrada</p>
        <p className="max-w-xs text-sm text-muted-foreground">Obrigado pelo interesse. Fique de olho nas próximas.</p>
      </Card>
    );
  }

  const description = survey.description?.trim();

  return (
    <PublicSurveyI18nProvider>
      <div className="mx-auto w-full max-w-xl space-y-4">
        {answered ? (
          <PublicSurveySubmitCelebration />
        ) : (
          <>
            <PromoHeroCard
              icon={MessageSquareHeart}
              iconMotion="pulse"
              eyebrow="Pesquisa de satisfação"
              title={survey.title}
              accentColor={accentColor}
              foregroundColor={foregroundColor}
            >
              {description ? (
                <>
                  <p id="survey-description" className={cn(!showDescription && 'line-clamp-2')}>
                    {parseSurveyDescription(description)}
                  </p>
                  {description.length > 110 ? (
                    <button
                      type="button"
                      className="mt-1 text-xs font-semibold underline underline-offset-4 opacity-90 hover:opacity-100"
                      aria-expanded={showDescription}
                      aria-controls="survey-description"
                      onClick={() => setShowDescription((v) => !v)}
                    >
                      {showDescription ? 'Mostrar menos' : 'Ler mais'}
                    </button>
                  ) : null}
                </>
              ) : (
                'Leva cerca de 1 minuto e ajuda a loja a melhorar.'
              )}
            </PromoHeroCard>
            <PublicSurveyWizard
              survey={survey}
              lang={SURVEY_LANG}
              identifierFromUrl={uniqueId}
              initialAnswers={initialAnswers}
              skipPrefilledUserInfo
              compact
              onSuccess={() => setAnswered(true)}
            />
          </>
        )}
      </div>
    </PublicSurveyI18nProvider>
  );
}
